import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SideNavLayout from "../customsidenav/sidenavlayout";
import NotAllowedPage from "../utils/reactpages/not allowed";
import Fax from "../fax/fax";
import Messaging from "../fax/messaging";
import OsaMessaging from "../fax/osaMessaging";
import WebRequestsTable from "../fax/webrequesttable";
import Onboarding from "../onboarding/onboarding";
import UnmatchedMapping from "../onboarding/unmatchedmapping";
import { FormInbox } from "../forms/staff/FormInboxPage";
import useInboxCount from "../forms/staff/useInboxCount";
import { practiceFeatures, practiceShortName } from "../config/practice";
import { inboxQueues, queueForPath } from "./inboxQueues";
import RequestsQueue from "./RequestsQueue";
import useInboxCounts from "./useInboxCounts";
import useFaxAssignedCount from "../fax/useFaxAssignedCount";

/*
 * The unified staff Inbox (owner 10-20, Step 1; features.unifiedInbox). A
 * queue rail on the left (with counts) and the queue on the right, one URL
 * per queue (inboxQueues.js). Requests is new (RequestsQueue.jsx); the other
 * queues are the existing pages, unchanged: Messaging, Fax / Voice mail /
 * Email (fax/fax.jsx, embedded), Form Inbox. The Requests queue keeps the old
 * Onboarding and Web Requests tabs one click away (?view=), so nothing they
 * offered goes missing during the parallel run.
 */

// Requests sub-views: the merged list, then the old tabs as they were.
export function requestViews(features = practiceFeatures()) {
  const views = [{ id: "", label: "All requests" }, { id: "onboarding", label: "Onboarding" }];
  if (features.websiteLeads) {
    views.push({ id: "web-appointment", label: "Appt requests (website)" });
    views.push({ id: "web-new-patient", label: "New patients (website)" });
  }
  if (features.dallasMigration) views.push({ id: "unmatched", label: "Unmatched patient mapping" });
  return views;
}

// The rail's number for a queue, or null (no badge) (review 10-20):
//   Requests  new requests          Faxes  faxes assigned to me (the old
//   Communication badge)            Forms  unreviewed submissions
//   All open  new requests + faxes assigned to me (= the side-nav badge)
// Messages has none: unread chats keep the header's chat badge, as before.
// The All open tiles show the queues' sizes instead (tileCount).
export function queueCount(id, counts, formsCount, faxAssigned) {
  const q = counts && counts.queues ? counts.queues : {};
  const mine = Number(faxAssigned) || 0;
  const hasMine = faxAssigned !== null && faxAssigned !== undefined;
  if (id === "all") return counts || hasMine ? ((counts && counts.total_new) || 0) + mine : null;
  if (id === "faxes") return hasMine ? mine : null;
  if (id === "forms") return formsCount ? formsCount.unreviewed : null;
  if (id === "requests") return q.requests ? q.requests.new : null;
  return null;
}

function RequestsView({ view, setView, openMessages }) {
  const views = requestViews();
  const current = views.some((v) => v.id === view) ? view : "";
  return (
    <div className="w-full">
      <div className="mb-3 flex gap-5 overflow-x-auto whitespace-nowrap border-b border-[#E4E4E7]" role="tablist" aria-label="Requests views">
        {views.map((v) => (
          <button
            key={v.id || "all"}
            type="button"
            role="tab"
            aria-selected={current === v.id}
            onClick={() => setView(v.id)}
            className={`shrink-0 pb-2 text-sm font-semibold ${current === v.id ? "border-b-2 border-[#50869A] text-[#50869A]" : "text-gray-500"}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {current === "" && <RequestsQueue onOpenView={setView} onOpenMessages={openMessages} />}
      {current !== "" && (
        <div className="w-full min-w-0 overflow-x-auto">
          {current === "onboarding" && <Onboarding embedded initialTab={0} />}
          {current === "web-appointment" && <WebRequestsTable type="appointment" />}
          {current === "web-new-patient" && <WebRequestsTable type="new_patient" />}
          {current === "unmatched" && <UnmatchedMapping />}
        </div>
      )}
    </div>
  );
}

// An All open tile: Requests new, Faxes to work, Messages unread conversations, Forms unreviewed.
export function tileCount(id, counts, formsCount) {
  const q = counts && counts.queues ? counts.queues : {};
  if (id === "forms") return formsCount ? formsCount.unreviewed : null;
  if (id === "faxes") return q.faxes ? q.faxes.open : null;
  return q[id] ? q[id].new : null;
}

function AllOpen({ queues, counts, formsCount, faxAssigned, go, onOpenView }) {
  const tiles = queues.filter((q) => q.id !== "all");
  const hasRequests = tiles.some((q) => q.id === "requests");
  return (
    <div className="w-full min-w-0 text-left">
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {tiles.map((q) => {
          const n = tileCount(q.id, counts, formsCount);
          const mine = q.id === "faxes" && faxAssigned ? Number(faxAssigned) : 0;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => go(q.path)}
              className="flex flex-col items-start rounded-xl border border-[#E4E7EC] bg-white px-4 py-3 text-left hover:border-[#1D2951]/30 hover:bg-[#F8FAFC]"
              data-testid={`inbox-tile-${q.id}`}
            >
              <span className="text-[13px] text-[#475467]">{q.label}</span>
              <span className="text-[22px] font-semibold text-[#1D2951]">{n === null || n === undefined ? "—" : n}</span>
              {mine > 0 && <span className="text-[12px] text-[#475467]">{mine} assigned to you</span>}
            </button>
          );
        })}
      </div>
      {hasRequests && (
        <>
          <div className="mb-2 text-sm font-semibold text-[#1D2951]">Newest open requests</div>
          <RequestsQueue compact onOpenView={onOpenView} onOpenMessages={() => go("/inbox/messages")} />
        </>
      )}
    </div>
  );
}

export default function InboxPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const privileges = useSelector((state) => (state.userPrivileges && state.userPrivileges.privileges) || []);
  const features = practiceFeatures();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queues = useMemo(() => inboxQueues(privileges, features), [privileges]);
  const queue = queueForPath(location.pathname, features);
  const allowed = Boolean(queue && queues.some((q) => q.id === queue.id));
  const counts = useInboxCounts(features.unifiedInbox && allowed);
  const formsCount = useInboxCount(Boolean(features.formWizard && allowed));
  const faxAssigned = useFaxAssignedCount(Boolean(features.fax && allowed && privileges.includes("Communication")));
  const view = searchParams.get("view") || "";

  useEffect(() => {
    document.title = `${practiceShortName()} - Inbox`;
  }, []);

  // Below 1024 px the rail is a horizontal strip: keep the open queue's button in view.
  const activeQueueRef = useRef(null);
  const activeQueueId = queue ? queue.id : "";
  useEffect(() => {
    const el = activeQueueRef.current;
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeQueueId]);

  const setView = (v) => {
    if (queue && queue.id !== "requests") {
      navigate(v ? `/inbox/requests?view=${encodeURIComponent(v)}` : "/inbox/requests");
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (v) next.set("view", v);
    else next.delete("view");
    setSearchParams(next);
  };

  if (!features.unifiedInbox || !allowed) return <NotAllowedPage />;

  // OSA's call-notes page (features.messagingPage; 7825 keeps the Inbox off) stays its own full
  // screen page, exactly as /messaging there. Everywhere else Messages renders inside the Inbox pane.
  if (queue.id === "messages" && features.messagingPage) {
    return <OsaMessaging onClose={() => navigate("/inbox")} />;
  }

  return (
    <div className="h-full">
      <SideNavLayout>
        {/* Responsive (owner 10-20): the rail on the left from 1024 px; below, the same buttons as a
            horizontal, scrollable strip above the queue. Nothing is wider than the page. */}
        <div className="flex w-full min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:gap-4" data-testid="inbox-page">
          <nav
            className="flex w-full min-w-0 shrink-0 gap-1 overflow-x-auto rounded-xl border border-[#E4E7EC] bg-white p-1 lg:sticky lg:top-[80px] lg:block lg:w-[190px] lg:overflow-visible lg:p-2"
            style={{ scrollbarWidth: "thin" }}
            aria-label="Inbox queues"
          >
            {queues.map((q) => {
              const n = queueCount(q.id, counts, formsCount, faxAssigned);
              const on = q.id === queue.id;
              return (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => navigate(q.path)}
                  aria-current={on ? "page" : undefined}
                  ref={on ? activeQueueRef : undefined}
                  data-testid={`inbox-queue-${q.id}`}
                  className={`flex h-11 shrink-0 items-center justify-between whitespace-nowrap rounded-lg px-3 text-left text-[14px] lg:mb-0.5 lg:h-9 lg:w-full ${
                    on ? "bg-[#1D2951] font-semibold text-white" : "text-[#344054] hover:bg-[#F2F4F7]"
                  }`}
                >
                  <span className="truncate">{q.label}</span>
                  {n > 0 && (
                    <span
                      title={q.id === "faxes" ? `${n} fax${n === 1 ? "" : "es"} assigned to you` : undefined}
                      data-testid={`inbox-count-${q.id}`}
                      className={`ml-2 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold leading-none ${
                        on ? "bg-white/20 text-white" : q.id === "faxes" ? "bg-[#1D2951]/[0.07] text-[#1D2951]" : "bg-[#FFF0E6] text-[#E17954]"
                      }`}
                    >
                      {n}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <div className="min-w-0 flex-1">
            {queue.id === "all" && (
              <AllOpen queues={queues} counts={counts} formsCount={formsCount} faxAssigned={faxAssigned} go={navigate} onOpenView={setView} />
            )}
            {queue.id === "requests" && <RequestsView view={view} setView={setView} openMessages={() => navigate("/inbox/messages")} />}
            {/* The existing pages, unchanged: a wide table scrolls inside its own box, never the page. */}
            {["faxes", "email", "voicemail", "forms"].includes(queue.id) && (
              <div className="w-full min-w-0 overflow-x-auto" data-testid="inbox-scroll-box">
                {queue.id === "faxes" && <Fax key="faxes" embedded initialTab={0} />}
                {queue.id === "email" && <Fax key="email" embedded initialTab={1} />}
                {queue.id === "voicemail" && <Fax key="voicemail" embedded initialTab={2} />}
                {queue.id === "forms" && <FormInbox />}
              </div>
            )}
            {queue.id === "messages" && <Messaging embedded />}
          </div>
        </div>
      </SideNavLayout>
    </div>
  );
}
